import { Texture } from 'pixi.js';

/**
 * Singleton texture cache for sprite management.
 * Prevents recreating textures/images on every render cycle.
 */

// In-memory texture cache (spriteDataUrl -> Texture)
const textureCache = new Map();

// Pending loads (spriteDataUrl -> Promise<Texture>)
const pendingLoads = new Map();

/**
 * Get or create a texture from a data URL.
 * Returns cached texture if already loaded.
 * @param {string} spriteDataUrl - Base64 data URL of the sprite image
 * @returns {Texture|null} - PixiJS Texture or null if not yet loaded
 */
export function getTexture(spriteDataUrl) {
  if (!spriteDataUrl) return null;
  return textureCache.get(spriteDataUrl) || null;
}

/**
 * Load a texture asynchronously via Image() for reliable dimensions.
 * Returns cached texture immediately if already loaded.
 * @param {string} spriteDataUrl - Base64 data URL of the sprite image
 * @returns {Promise<Texture>} - Promise resolving to the loaded Texture
 */
export async function loadTexture(spriteDataUrl) {
  if (!spriteDataUrl) {
    return Promise.reject(new Error('No sprite URL provided'));
  }

  // Return cached texture immediately
  if (textureCache.has(spriteDataUrl)) {
    return textureCache.get(spriteDataUrl);
  }

  // Return pending load if already in progress
  if (pendingLoads.has(spriteDataUrl)) {
    return pendingLoads.get(spriteDataUrl);
  }

  // Start new load
  const loadPromise = new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      try {
        const texture = Texture.from(img);
        textureCache.set(spriteDataUrl, texture);
        pendingLoads.delete(spriteDataUrl);
        resolve(texture);
      } catch (e) {
        pendingLoads.delete(spriteDataUrl);
        reject(e);
      }
    };

    img.onerror = (e) => {
      pendingLoads.delete(spriteDataUrl);
      reject(new Error('Failed to load sprite image'));
    };

    img.src = spriteDataUrl;
  });

  pendingLoads.set(spriteDataUrl, loadPromise);
  return loadPromise;
}

/**
 * Preload multiple sprites at once (e.g., on app start or layout load).
 * @param {string[]} spriteUrls - Array of sprite data URLs to preload
 * @returns {Promise<void>}
 */
export async function preloadSprites(spriteUrls) {
  const uniqueUrls = [...new Set(spriteUrls.filter(Boolean))];
  await Promise.all(uniqueUrls.map(url => loadTexture(url).catch(() => null)));
}

/**
 * Check if a texture is cached and ready for immediate use.
 * @param {string} spriteDataUrl - Base64 data URL
 * @returns {boolean}
 */
export function isTextureCached(spriteDataUrl) {
  return textureCache.has(spriteDataUrl);
}

/**
 * Get the original Image dimensions for a cached texture.
 * Useful for calculating sprite scaling.
 * @param {string} spriteDataUrl 
 * @returns {{ width: number, height: number } | null}
 */
export function getTextureDimensions(spriteDataUrl) {
  const texture = textureCache.get(spriteDataUrl);
  if (!texture) return null;
  return { width: texture.width, height: texture.height };
}

/**
 * Remove a specific texture from the cache.
 * @param {string} spriteDataUrl 
 */
export function removeTexture(spriteDataUrl) {
  const texture = textureCache.get(spriteDataUrl);
  if (texture) {
    texture.destroy(true);
    textureCache.delete(spriteDataUrl);
  }
  pendingLoads.delete(spriteDataUrl);
}

/**
 * Clear all cached textures.
 * Call when unloading a project or on memory pressure.
 */
export function clearCache() {
  for (const texture of textureCache.values()) {
    try {
      texture.destroy(true);
    } catch (e) {
      // Ignore destroy errors
    }
  }
  textureCache.clear();
  pendingLoads.clear();
}

/**
 * Get cache statistics for debugging.
 * @returns {{ cached: number, pending: number }}
 */
export function getCacheStats() {
  return {
    cached: textureCache.size,
    pending: pendingLoads.size,
  };
}
